import { Component, inject, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { BaseInputControl } from '../base-input-control/base-input-control.component';
import { NavigatorComponent } from '../../../../features/interview/navigator/navigator.component';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { Charity, CharityService, InterviewState, Question } from "@bequeathed/ngx-services";
import { LoggerService } from "@bequeathed/ngx-app-core";
import { NgClass } from "@angular/common";
import { FormControlPipe, PageUtil, ValidationUtils } from "@bequeathed/ngx-utils";
import { BaseControl } from "../base-control/base-control.component";
import { CheckboxControl } from "../checkbox-control/checkbox-control.component";
import { ErrorInfo } from "../../base-form/base-form.component";

const LOG: LoggerService = LoggerService.get('CharitySearchControl');

/**
 * Control to allow users to search for a charity that they want to add rather than entering the details manually.
 *
 * @author Dan Bennett (dbennett)
 */
@Component({
    selector: 'charity-search-control',
    templateUrl: './charity-search-control.component.html',
    styleUrls: [
        './charity-search-control.component.sass',
        '../checkbox-control/checkbox-control.component.sass',
        '../../base-form/base-form.component.sass',
        '../base-input-control/base-input-control.component.sass'
    ],
    standalone: true,
    imports: [
        ReactiveFormsModule,
        NgClass,
        FormControlPipe,
        CheckboxControl
    ],
    encapsulation: ViewEncapsulation.None
})
export class CharitySearchControl extends BaseInputControl implements OnInit {

    @Input() navigator: NavigatorComponent | undefined;

    charitySearchControl: UntypedFormControl | undefined;
    charities: Charity[] | undefined;
    previousSearch: string | undefined;

    selectedCharities: Charity[] | undefined;

    charityService = inject(CharityService);

    changedMind: Question | undefined;
    @ViewChild("changedMindControl") changedMindControl: BaseControl | undefined;

    constructor() {
        super();
        this.inputClass = '';
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.changedMind = undefined;

        this.charities = [];
        this.selectedCharities = [];
        this.charitySearchControl = new UntypedFormControl();
        // ToDo :: Get the rest of the details
        if (this.previousAnswer?.length) {
            this.charityService
                .loadPreselectedCharities(this.previousAnswer[0].split('|'))
                .then((charities: Charity[]) => this.selectedCharities = charities);
        }
        this.charitySearchControl.valueChanges
            .pipe(debounceTime(500))
            .subscribe((newValue: string) => {
                // Only search if:
                // - value is blank OR value is at least 3 characters long AND is not the same as previously searched
                LOG.trace('init', `Checking value [${newValue}]...`);
                if (ValidationUtils.validMinLength(newValue, 2) && ValidationUtils.isNotEqual(this.previousSearch, newValue)) {
                    this.previousSearch = newValue;
                    this.searchCharityByName(newValue).then(() => LOG.debug('searchCharities', `Found ${this.charities!.length} charities`));
                }
            });
    }

    async searchCharityByName(name: string): Promise<void> {
        this.charities = await this.charityService.findPayPalCharityByName(name, true);

        // remove already selected charities from display
        for (let i = 0; i < this.selectedCharities!.length; i++) {
            for (let j = 0; j < this.charities.length; i++) {
                if (this.charities[j].name === this.selectedCharities![i].name) {
                    this.charities.splice(j, 1);
                }
            }
        }
    }

    override focus(): void {
        const nativeElement: any = PageUtil.findNativeElement('charity-search');
        if (nativeElement) {
            nativeElement.focus();
        }
    }

    removeSelectedCharity(charity: Charity) {
        for (let i = 0; i < this.selectedCharities!.length; i++) {
            if (this.selectedCharities![i].name === charity.name) {
                this.charities!.push(charity);
                this.selectedCharities!.splice(i, 1);
            }
        }

        this.charities!.sort((c: Charity, d: Charity) => c.name!.localeCompare(d.name!));
        this.onValueChange();
    }

    selectCharity(charity: Charity) {
        for (let i = 0; i < this.charities!.length; i++) {
            if (this.charities![i].name === charity.name) {
                this.selectedCharities!.push(charity);
                this.charities!.splice(i, 1);
            }
        }
        this.selectedCharities!.sort((c: Charity, d: Charity) => c.name!.localeCompare(d.name!));
        this.onValueChange();
    }

    override onValueChange() {
        this.control.setValue(this.selectedCharities)
        super.setModelValue(this.control.value)
    }

    override async validate(): Promise<ErrorInfo[] | undefined> {
        let errors = await super.validate();
        if (!errors?.length) {
            if (!this.selectedCharities?.length && !this.changedMindControl!.control.value[0]) {
                errors = [{
                    field: this.questionId,
                    error: 'required.charity_gifts.other',
                } as ErrorInfo];
                this.control.setErrors([{invalid: true}]);
                this.control?.markAllAsTouched();
                this.control?.updateValueAndValidity();
            }
        }
        return errors;
    }

    override applyValue(state: InterviewState | Question): void {
        if (state instanceof Question) {
            return;
        }

        if (this.changedMindControl!.control.value[0]) {
            // Handle changed_mind checkbox question by swapping current question
            const currentQuestion = state.currentQuestion;
            // @ts-expect-error only after change_mind question
            state.currentQuestion = currentQuestion!.questions!.find((q: Question) => {
                if (q.id?.endsWith('changed_mind')) {
                    q.userResponse = this.changedMindControl!.question.acceptedValues.keys().next().value;
                    return q
                }
            })
        } else {
            // For each charity apply the extra details for the group
            const currentQuestion = state.currentQuestion;
            currentQuestion!.groupedQuestions = new Map<number, Question[]>();
            const selectedCharityNames = this.selectedCharities!.map((c: Charity, index: number) => {
                const questions: Question[] = [];
                currentQuestion!.questions!.forEach((q: Question) => {
                    let answer: string | undefined = '';
                    let type = 'METADATA';
                    if (q.id?.endsWith('name')) {
                        answer = c.name;
                        type = 'DISPLAY';
                    } else if (q.id?.endsWith('number')) {
                        answer = c.number;
                    } else if (q.id?.endsWith('line1')) {
                        answer = c.addressLine1;
                    } else if (q.id?.endsWith('line2')) {
                        answer = c.addressLine2;
                    } else if (q.id?.endsWith('city')) {
                        answer = c.city;
                    } else if (q.id?.endsWith('county')) {
                        answer = c.county;
                    } else if (q.id?.endsWith('postcode')) {
                        answer = c.postcode;
                    }
                    questions.push(new Question({
                        id: `${q?.sourceId}[${index}].${q.id}`,
                        type: type,
                        sourceId: q.sourceId,
                        calculatedResponse: answer,
                        userResponse: answer
                    }));
                });

                currentQuestion!.groupedQuestions!.set(index, questions);
                return c.name;
            })
                .join('|');

            currentQuestion!.userResponse = selectedCharityNames;
            currentQuestion!.calculatedResponse = selectedCharityNames;
        }
    }

    findChangedMindQuestion(): Question | undefined {
        if (this.question.questions?.length && !this.changedMind) {
            this.changedMind = this.question.questions.find((q: Question) => q.id!.endsWith('.changed_mind'));
        }
        return this.changedMind;
    }
}
